/**
 * Domains list page with verification wizard.
 */

import { useState } from 'react';
import { useDomains, useCreateDomain, useDeleteDomain, useVerifyDomain } from '@/hooks/useDomains';
import { DataTable } from '@/components/tables/data-table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DomainForm, type DomainFormData } from '@/components/forms/domain-form';
import { DNSInstructionsModal } from '@/components/modals/dns-instructions-modal';
import { ConfirmationModal } from '@/components/modals/confirmation-modal';
import { DomainStatus } from '@/components/domains/domain-status';
import { Plus, Trash2 } from 'lucide-react';
import type { DomainResponse, DomainStatusResponse } from '@/api/schemas';
import type { Column } from '@/components/tables/data-table';
import './list.scss';

export function DomainsList() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDNSModal, setShowDNSModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<DomainResponse | null>(null);
  const [domainToDelete, setDomainToDelete] = useState<DomainResponse | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<DomainStatusResponse | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  
  const { data, isLoading } = useDomains();
  const createDomain = useCreateDomain();
  const deleteDomain = useDeleteDomain();
  const verifyDomain = useVerifyDomain();

  const handleCreateDomain = async (formData: DomainFormData) => {
    try {
      setFormError(null);
      const newDomain = await createDomain.mutateAsync(formData);
      setShowAddModal(false);
      setSelectedDomain(newDomain);
      setShowDNSModal(true);
    } catch (error: unknown) {
      // Handle different error types
      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = error.message as string;
        if (errorMessage.includes('already exists')) {
          setFormError('This domain is already added to your account.');
        } else if (errorMessage.includes('limit')) {
          setFormError('You\'ve reached your plan\'s domain limit. Please upgrade your plan.');
        } else {
          setFormError(errorMessage || 'Failed to add domain. Please try again.');
        }
      } else {
        setFormError('Failed to add domain. Please try again.');
      }
    }
  };

  const handleVerifyDomain = async () => {
    if (!selectedDomain) return;
    
    try {
      const status = await verifyDomain.mutateAsync(selectedDomain.domain_id);
      setVerificationStatus(status);
      
      // If verified, close modal after a short delay
      if (status.verified) {
        setTimeout(() => {
          setShowDNSModal(false);
          setSelectedDomain(null);
          setVerificationStatus(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Verification failed:', error);
      // Error handling is done by the modal component
    }
  };

  const handleDeleteClick = (domain: DomainResponse) => {
    setDomainToDelete(domain);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!domainToDelete) return;

    try {
      await deleteDomain.mutateAsync(domainToDelete.domain_id);
      setShowDeleteModal(false);
      setDomainToDelete(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete domain';
      setFormError(errorMessage);
      console.error('Failed to delete domain:', error);
      // Keep modal open on error so user can see the error or try again
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDomainToDelete(null);
    setFormError(null);
  };

  const handleVerifyFromList = async (domain: DomainResponse) => {
    setSelectedDomain(domain);
    setShowDNSModal(true);
    try {
      const status = await verifyDomain.mutateAsync(domain.domain_id);
      setVerificationStatus(status);
    } catch (error) {
      console.error('Verification failed:', error);
      // Modal will show with current status
    }
  };

  const columns: Column<DomainResponse>[] = [
    {
      key: 'domain_name',
      header: 'Domain',
      render: (domain) => <span className="font-medium">{domain.domain_name}</span>,
    },
    {
      key: 'dns_method',
      header: 'DNS Method',
      render: (domain) => (
        <span className="badge badge--info">
          {domain.dns_method || 'N/A'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (domain) => (
        <DomainStatus
          domain={domain}
          onVerify={() => handleVerifyFromList(domain)}
          isVerifying={verifyDomain.isPending && selectedDomain?.domain_id === domain.domain_id}
        />
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (domain) => new Date(domain.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (domain) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(domain);
          }}
          className="domains-list__delete"
          title="Delete domain"
          aria-label="Delete domain"
        >
          <Trash2 />
        </button>
      ),
    },
  ];

  return (
    <div className="domains-list">
      <div className="domains-list__header">
        <div>
          <h1 className="h1">Domains</h1>
          <p className="subtitle">Manage your tracking domains</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus style={{ marginRight: 'var(--of-space-2)', width: '1rem', height: '1rem' }} />
          Add Domain
        </Button>
      </div>

      <Card>
        <DataTable
          columns={columns}
          data={data?.domains || []}
          isLoading={isLoading}
        />
      </Card>

      {/* Domain Creation Modal */}
      {showAddModal && (
        <div className="domains-list__modal" role="dialog" aria-labelledby="domain-modal-title">
          <div className="domains-list__modal-backdrop" onClick={() => {
            setShowAddModal(false);
            setFormError(null);
          }} />
          <div className="domains-list__modal-content">
            <h2 id="domain-modal-title" className="h3" style={{ marginBottom: 'var(--of-space-4)' }}>
              Add Domain
            </h2>
            <DomainForm
              onSubmit={handleCreateDomain}
              onCancel={() => {
                setShowAddModal(false);
                setFormError(null);
              }}
              isLoading={createDomain.isPending}
              error={formError}
            />
          </div>
        </div>
      )}

      {/* DNS Instructions Modal */}
      {showDNSModal && selectedDomain && (
        <DNSInstructionsModal
          domain={selectedDomain}
          onVerify={handleVerifyDomain}
          onClose={() => {
            setShowDNSModal(false);
            setSelectedDomain(null);
            setVerificationStatus(null);
          }}
          isVerifying={verifyDomain.isPending}
          verificationStatus={verificationStatus || undefined}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && domainToDelete && (
        <ConfirmationModal
          title="Delete Domain"
          message={`Are you sure you want to delete "${domainToDelete.domain_name}"? This action cannot be undone and all placements using this domain will be affected.`}
          confirmLabel="Delete Domain"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isLoading={deleteDomain.isPending}
        />
      )}
    </div>
  );
}
